// src/components/forum/TopicList.tsx
import { AD_SLOTS } from "../../config/adSlots";
import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  fetchForums,
  subscribeTopics,
  createTopic,
  editTopic,
  deleteTopic,
  fetchMessageCount,
} from "../../services/forumService";
import { auth } from "../../services/firebaseService";
import AdBanner from "../ads/AdBanner";

interface Topic {
  id: string;
  title: string;
  content?: string;
  authorId: string;
  authorName: string;
  createdAt?: { seconds: number; nanoseconds: number };
  editedAt?: { seconds: number; nanoseconds: number };
}

export default function TopicList() {
  const { forumId } = useParams<{ forumId: string }>();
  const navigate = useNavigate();
  const user = auth.currentUser;

  const [forumName, setForumName] = useState("…");
  const [topics, setTopics] = useState<Topic[]>([]);
  const [messageCounts, setMessageCounts] = useState<Record<string, number>>(
    {}
  );
  const [newTitle, setNewTitle] = useState("");
  const [newContent, setNewContent] = useState("");
  const [editing, setEditing] = useState<Record<string, boolean>>({});

  // 1) Load this forum's name once
  useEffect(() => {
    if (!forumId) return;
    fetchForums()
      .then((fs) => {
        const f = fs.find((x) => x.id === forumId);
        if (f) setForumName(f.name);
      })
      .catch(console.error);
  }, [forumId]);

  // 2) Subscribe to topics in real‑time
  useEffect(() => {
    if (!forumId) return;
    const unsub = subscribeTopics(forumId, setTopics);
    return unsub;
  }, [forumId]);

  // 3) Whenever topics update, fetch their message counts
  useEffect(() => {
    if (!forumId || topics.length === 0) return;
    (async () => {
      try {
        const pairs = await Promise.all(
          topics.map(
            async (t) =>
              [t.id, await fetchMessageCount(forumId, t.id)] as [string, number]
          )
        );
        setMessageCounts(Object.fromEntries(pairs));
      } catch (err) {
        console.error("Failed to load message counts", err);
      }
    })();
  }, [forumId, topics]);

  // 4) Require sign‑in
  if (!user) {
    return (
      <div className="p-6 text-center text-white">
        Please{" "}
        <Link to="/" className="text-blue-400 underline">
          sign in
        </Link>{" "}
        to view topics.
      </div>
    );
  }

  // 5) Handlers
  const handleCreate = async () => {
    if (!newTitle.trim()) return;
    await createTopic(forumId!, newTitle.trim(), newContent.trim(), user.uid);
    setNewTitle("");
    setNewContent("");
  };

  const startEdit = (id: string) => setEditing((e) => ({ ...e, [id]: true }));
  const cancelEdit = (id: string) => setEditing((e) => ({ ...e, [id]: false }));
  const saveEdit = async (id: string, title: string, content: string) => {
    await editTopic(forumId!, id, { title, content });
    cancelEdit(id);
  };
  const removeTopic = async (id: string) => {
    if (window.confirm("Delete this topic?")) {
      await deleteTopic(forumId!, id);
    }
  };

  // 6) Render
  return (
    <div className="p-6 space-y-6">
      <button
        className="text-blue-400 hover:underline"
        onClick={() => navigate(-1)}
      >
        ← Back to Forums
      </button>

      <h1 className="text-3xl font-bold text-white">{forumName}</h1>

      <div className="space-y-4">
        {topics.map((t) => (
          <div
            key={t.id}
            className="flex bg-gray-800 rounded-lg overflow-hidden"
          >
            {/* Message count */}
            <div className="flex flex-col items-center bg-gray-700 px-3 py-4">
              <span className="text-white text-lg font-bold">
                {messageCounts[t.id] ?? 0}
              </span>
              <span className="text-gray-400 text-sm">msgs</span>
            </div>

            {/* Topic content */}
            <div className="flex-1 p-4">
              {editing[t.id] ? (
                <TopicEditor
                  initialTitle={t.title}
                  initialContent={t.content || ""}
                  onSave={(tt, cc) => saveEdit(t.id, tt, cc)}
                  onCancel={() => cancelEdit(t.id)}
                />
              ) : (
                <>
                  <Link
                    to={`/forums/${forumId}/topics/${t.id}`}
                    className="text-xl font-semibold text-white hover:text-blue-300"
                  >
                    {t.title}
                  </Link>

                  {t.content && (
                    <p className="mt-1 text-gray-300 line-clamp-2">
                      {t.content}
                    </p>
                  )}

                  <div className="mt-2 text-sm text-gray-500 flex items-center space-x-2">
                    <span>by {t.authorName}</span>
                    <span>•</span>
                    <span>
                      {new Date(
                        ((t.editedAt ?? t.createdAt)?.seconds || 0) * 1000
                      ).toLocaleString()}
                    </span>
                    {t.editedAt && <span>(edited)</span>}
                  </div>

                  {t.authorId === user.uid && (
                    <div className="mt-2 flex space-x-4 text-sm">
                      <button
                        className="text-yellow-400 hover:underline"
                        onClick={() => startEdit(t.id)}
                      >
                        Edit
                      </button>
                      <button
                        className="text-red-400 hover:underline"
                        onClick={() => removeTopic(t.id)}
                      >
                        Delete
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        ))}
      </div>
      <div className="my-6">
        <AdBanner
          adSlot={AD_SLOTS.forumTopics}
          adFormat="horizontal"
          fullWidthResponsive={true}
          style={{ maxWidth: '728px', margin: '0 auto' }}
        />
      </div>

      {/* New Topic Form */}
      <div className="bg-gray-700 p-6 rounded-lg space-y-4">
        <h2 className="text-2xl font-semibold text-white">New Topic</h2>
        <input
          type="text"
          value={newTitle}
          onChange={(e) => setNewTitle(e.target.value)}
          placeholder="Title"
          className="w-full px-4 py-2 bg-gray-600 text-white rounded"
        />
        <textarea
          rows={3}
          value={newContent}
          onChange={(e) => setNewContent(e.target.value)}
          placeholder="Content (optional)"
          className="w-full px-4 py-2 bg-gray-600 text-white rounded"
        />
        <button
          onClick={handleCreate}
          disabled={!newTitle.trim()}
          className="px-6 py-2 bg-green-600 hover:bg-green-700 text-white rounded disabled:opacity-50"
        >
          Post Topic
        </button>
      </div>
    </div>
  );
}

// Inline editor used when editing a topic
function TopicEditor({
  initialTitle,
  initialContent,
  onSave,
  onCancel,
}: {
  initialTitle: string;
  initialContent: string;
  onSave: (title: string, content: string) => void;
  onCancel: () => void;
}) {
  const [title, setTitle] = useState(initialTitle);
  const [content, setContent] = useState(initialContent);

  return (
    <div className="space-y-3">
      <input
        type="text"
        className="w-full px-3 py-2 bg-gray-600 text-white rounded"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
      />
      <textarea
        rows={2}
        className="w-full px-3 py-2 bg-gray-600 text-white rounded"
        value={content}
        onChange={(e) => setContent(e.target.value)}
      />
      <div className="flex space-x-2">
        <button
          className="px-4 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded"
          onClick={() => onSave(title, content)}
        >
          Save
        </button>
        <button
          className="px-4 py-1 bg-gray-500 hover:bg-gray-600 text-white rounded"
          onClick={onCancel}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
